import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TimecodeNotes.css';

interface Note {
  id: number;
  timecode: string;
  text: string;
  timestamp: number;
}

interface Command {
  execute: () => void;
  undo: () => void;
  description: string;
}

export const TimecodeNotes: React.FC = () => {
  // App State
  const [isPlaying, setIsPlaying] = useState(false);
  const [projectName, setProjectName] = useState(() => {
    return localStorage.getItem('timecodeNotes_projectName') || 'Untitled Project';
  });
  const [fps, setFps] = useState(24);
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteInput, setNoteInput] = useState('');
  const [noteTimecode, setNoteTimecode] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  // Segment Editing State
  const [editingSegment, setEditingSegment] = useState<string | null>(null); // 'hh', 'mm', 'ss', 'ff'
  const [segmentEditValue, setSegmentEditValue] = useState('');
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notification, setNotification] = useState<string | null>(null);

  // Refs for timer and logic
  const startTimeRef = useRef(0);
  const pauseTimeRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const [displayTimecode, setDisplayTimecode] = useState('00:00:00:00');
  const customStartTimecodeRef = useRef('00:00:00:00');
  const startFromZeroRef = useRef(true);

  // Undo/Redo State
  const historyRef = useRef<Command[]>([]);
  const historyIndexRef = useRef(-1);

  // Timecode calculation
  const padZero = (num: number) => num.toString().padStart(2, '0');

  const calculateTimecode = useCallback((elapsedMs: number) => {
    const currentFps = fps;
    const tcSegments = customStartTimecodeRef.current.split(':').map(Number);
    const offsetFrames = (((((tcSegments[0] * 60) + tcSegments[1]) * 60) + tcSegments[2]) * currentFps) + tcSegments[3];
    const framesPerMs = currentFps / 1000;
    const totalFrames = Math.floor(elapsedMs * framesPerMs) + offsetFrames;

    const frames = Math.floor(totalFrames % currentFps);
    const totalSeconds = Math.floor(totalFrames / currentFps);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}:${padZero(frames)}`;
  }, [fps]);

  const updateDisplay = useCallback(() => {
    if (editingSegment) return; // Don't update display while editing a segment

    const elapsedMs = isPlaying
      ? performance.now() - startTimeRef.current
      : pauseTimeRef.current || 0;

    const newTimecode = calculateTimecode(elapsedMs);
    setDisplayTimecode(newTimecode);

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateDisplay);
    }
  }, [isPlaying, calculateTimecode, editingSegment]);

  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateDisplay);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      updateDisplay();
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, updateDisplay]);

  // Actions
  const togglePlayPause = () => {
    if (editingSegment) return; // Can't play/pause while editing segments

    if (isPlaying) {
      pauseTimeRef.current = performance.now() - startTimeRef.current;
      setIsPlaying(false);
    } else {
      if (startFromZeroRef.current) {
        pauseTimeRef.current = 0;
        startFromZeroRef.current = false;
      }
      startTimeRef.current = performance.now() - pauseTimeRef.current;
      setIsPlaying(true);
    }
  };

  const stop = () => {
    setIsPlaying(false);
    pauseTimeRef.current = 0;
    startTimeRef.current = 0;
    startFromZeroRef.current = true;
    setDisplayTimecode(customStartTimecodeRef.current); // Reset to custom start time
  };

  const deleteNote = (id: number) => {
    const noteToDelete = notes.find(n => n.id === id);
    if (!noteToDelete) return;

    const command: Command = {
      execute: () => {
        setNotes(prev => prev.filter(n => n.id !== id));
      },
      undo: () => {
        setNotes(prev => {
          const updated = [...prev, noteToDelete];
          return updated.sort((a, b) => a.timestamp - b.timestamp);
        });
      },
      description: 'Delete Note'
    };

    executeCommand(command);
  };

  const deleteAllNotes = () => {
    if (notes.length === 0) return;
    if (!confirm('Are you sure you want to delete all notes?')) return;

    const notesSnapshot = [...notes];
    const command: Command = {
      execute: () => setNotes([]),
      undo: () => setNotes(notesSnapshot),
      description: 'Delete All Notes'
    };

    executeCommand(command);
  };

  const addNote = useCallback(() => {
    const text = noteInput.trim();
    if (!text) return;

    const currentTimecode = noteTimecode || displayTimecode;
    const newNote: Note = {
      id: Date.now(),
      timecode: currentTimecode,
      text: text,
      timestamp: isPlaying ? performance.now() - startTimeRef.current : pauseTimeRef.current
    };

    const command: Command = {
      execute: () => {
        setNotes(prev => {
          const updated = [...prev, newNote];
          return updated.sort((a, b) => a.timestamp - b.timestamp);
        });
      },
      undo: () => {
        setNotes(prev => prev.filter(n => n.id !== newNote.id));
      },
      description: 'Add Note'
    };

    executeCommand(command);
    setNoteInput('');
    setNoteTimecode(null);
  }, [noteInput, displayTimecode, noteTimecode, isPlaying]);

  const executeCommand = (command: Command) => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    }

    command.execute();
    historyRef.current.push(command);
    if (historyRef.current.length > 100) {
      historyRef.current.shift();
    } else {
      historyIndexRef.current = historyRef.current.length - 1;
    }
    showNotification(command.description);
  };

  const undo = useCallback(() => {
    if (historyIndexRef.current >= 0) {
      const command = historyRef.current[historyIndexRef.current];
      command.undo();
      historyIndexRef.current--;
      showNotification(`Undo: ${command.description}`);
    }
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current++;
      const command = historyRef.current[historyIndexRef.current];
      command.execute();
      showNotification(`Redo: ${command.description}`);
    }
  }, []);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD+Z / CTRL+Z
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // CMD+SHIFT+Z / CTRL+SHIFT+Z
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        redo();
      }
      // Ctrl/Cmd + Enter
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        if (document.activeElement?.tagName !== 'INPUT') {
          e.preventDefault();
          if (isPlaying) {
            setNoteTimecode(displayTimecode);
            const input = document.getElementById('noteInput');
            if (input) (input as HTMLInputElement).focus();
          }
        }
      }
      // Space
      if (e.code === 'Space' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        !editingSegment) {
        e.preventDefault();
        togglePlayPause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, togglePlayPause, isPlaying, displayTimecode, editingSegment]);

  // Segment Editing Logic
  const validateSegment = (type: string, val: string) => {
    const n = Number(val);
    if (isNaN(n) || val.length > 2) return false;
    if (type === 'hh') return n >= 0 && n <= 23;
    if (type === 'mm' || type === 'ss') return n >= 0 && n <= 59;
    if (type === 'ff') return n >= 0 && n < Math.floor(fps);
    return false;
  };

  const startSegmentEdit = (type: string) => {
    if (isPlaying) return;
    const currentTc = displayTimecode.split(':');
    const segmentIdx = ['hh', 'mm', 'ss', 'ff'].indexOf(type);
    setEditingSegment(type);
    setSegmentEditValue(currentTc[segmentIdx]);
    setErrorVisible(false);
  };

  const commitSegmentEdit = (type: string, val: string, navigateDir: number = 0) => {
    const paddedVal = val.padStart(2, '0');
    if (validateSegment(type, paddedVal)) {
      const currentTc = displayTimecode.split(':');
      const segmentIdx = ['hh', 'mm', 'ss', 'ff'].indexOf(type);
      currentTc[segmentIdx] = paddedVal;
      const newTc = currentTc.join(':');

      setDisplayTimecode(newTc);
      customStartTimecodeRef.current = newTc;
      setEditingSegment(null);
      setErrorVisible(false);

      if (navigateDir !== 0) {
        const segments = ['hh', 'mm', 'ss', 'ff'];
        const nextIdx = (segmentIdx + navigateDir + 4) % 4;
        setTimeout(() => startSegmentEdit(segments[nextIdx]), 0);
      }
    } else {
      setErrorMessage(`Invalid value for ${type.toUpperCase()}`);
      setErrorVisible(true);
    }
  };

  const handleExport = () => {
    if (notes.length === 0) {
      alert('No notes to export');
      return;
    }
    const safeProjectName = projectName.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `TCN_${safeProjectName}_${dateStr}.csv`;

    let csvContent = `Project: ${projectName}\nTimecode,Note\n`;
    notes.forEach(note => {
      const escapedNote = `"${note.text.replace(/"/g, '""')}"`;
      csvContent += `${note.timecode},${escapedNote}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const lines = content.split('\n').filter(line => line.trim() !== '');
        let startIndex = 0;

        if (lines[0].toLowerCase().startsWith('project:')) {
          setProjectName(lines[0].substring(8).trim());
          startIndex = 1;
        }
        if (startIndex < lines.length && (lines[startIndex].toLowerCase().includes('timecode') || lines[startIndex].toLowerCase().includes('note'))) {
          startIndex++;
        }

        const importedNotes: Note[] = [];
        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i];
          const commaIdx = line.indexOf(',');
          if (commaIdx === -1) continue;

          let timecode = line.slice(0, commaIdx).trim();
          let text = line.slice(commaIdx + 1).trim();

          if (text.startsWith('"') && text.endsWith('"')) {
            text = text.slice(1, -1).replace(/""/g, '"');
          }

          if (timecode.match(/^\d{2}:\d{2}:\d{2}:\d{2}$/) && text) {
            importedNotes.push({
              id: Date.now() + i,
              timecode,
              text,
              timestamp: 0
            });
          }
        }
        setNotes(importedNotes);
        showNotification(`Imported ${importedNotes.length} notes`);
      } catch (err) {
        alert('Error importing notes.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const tcArray = displayTimecode.split(':');

  return (
    <div className="timecode-notes-container">
      <div className="app-container">
        <div className="stripe-bar"></div>
        <div className="timecode-display">
          {['hh', 'mm', 'ss', 'ff'].map((type, idx) => (
            <React.Fragment key={type}>
              <div className="tc-segment-container" onClick={() => startSegmentEdit(type)}>
                {editingSegment === type ? (
                  <span
                    className="tc-edit-input"
                    contentEditable
                    suppressContentEditableWarning
                    tabIndex={0}
                    onBlur={(e) => commitSegmentEdit(type, e.currentTarget.textContent || '')}
                    onKeyDown={(e) => {
                      // Allow only numbers and navigation keys
                      if (
                        !e.ctrlKey && !e.metaKey && !e.altKey &&
                        e.key.length === 1 && !/[0-9]/.test(e.key)
                      ) {
                        e.preventDefault();
                        return;
                      }
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingSegment(null);
                      }
                      if (e.key === 'Tab') {
                        e.preventDefault();
                        commitSegmentEdit(type, e.currentTarget.textContent || '', e.shiftKey ? -1 : 1);
                      }
                      if (e.key === 'ArrowRight') {
                        const sel = window.getSelection();
                        // If at the end of the text, move to next segment
                        if (sel && sel.focusOffset === e.currentTarget.textContent?.length) {
                          e.preventDefault();
                          commitSegmentEdit(type, e.currentTarget.textContent || '', 1);
                        }
                      }
                      if (e.key === 'ArrowLeft') {
                        const sel = window.getSelection();
                        // If at the start of the text, move to prev segment
                        if (sel && sel.focusOffset === 0) {
                          e.preventDefault();
                          commitSegmentEdit(type, e.currentTarget.textContent || '', -1);
                        }
                      }
                    }}
                    ref={(el) => {
                      if (el && editingSegment === type && el.textContent !== segmentEditValue) {
                        el.textContent = segmentEditValue;
                        // Select all text only on initial mount/focus
                        requestAnimationFrame(() => {
                          el.focus();
                          const range = document.createRange();
                          range.selectNodeContents(el);
                          const sel = window.getSelection();
                          sel?.removeAllRanges();
                          sel?.addRange(range);
                        });
                      }
                    }}
                  >
                  </span>
                ) : (
                  <span className="tc-segment">{tcArray[idx]}</span>
                )}
                <span className="tc-segment-label">{type.toUpperCase()}</span>
              </div>
              {idx < 3 && ':'}
            </React.Fragment>
          ))}
        </div>

        {errorVisible && <div id="tc-error">{errorMessage}</div>}

        <div className="controls">
          <div className="project-name">
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Untitled Project"
              maxLength={50}
            />
          </div>
          <div className="fps-selector">
            <label htmlFor="fps">FPS:</label>
            <select id="fps" value={fps} onChange={(e) => setFps(parseFloat(e.target.value))}>
              <option value="23.976">23.976</option>
              <option value="24">24</option>
              <option value="25">25</option>
              <option value="29.97">29.97</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="59.94">59.94</option>
              <option value="60">60</option>
            </select>
          </div>
          <div className="button-group">
            <button className="cinematic-button" onClick={togglePlayPause}>
              {isPlaying ? '⏸️ Pause' : '▶️ Start'}
            </button>
            <button className="cinematic-button" onClick={stop}>Stop</button>
          </div>
        </div>

        <div className="note-area">
          <div className="notes-header">
            <h3>Notes</h3>
            <div className="header-actions">
              <button className="cinematic-button" onClick={handleExport}>💾 Export Notes</button>
              <button className="cinematic-button" onClick={() => document.getElementById('fileInput')?.click()}>📂 Import Notes</button>
              <button className="cinematic-button danger" onClick={deleteAllNotes}>🗑️ Delete All Notes</button>
            </div>
          </div>
          <div className="current-note">
            <input
              type="text"
              id="noteInput"
              value={noteInput}
              onChange={(e) => {
                setNoteInput(e.target.value);
                if (e.target.value && !noteTimecode) {
                  setNoteTimecode(displayTimecode);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addNote();
                if (e.key === 'Escape') {
                  setNoteInput('');
                  setNoteTimecode(null);
                }
              }}
              placeholder="Press 'Ctrl/Cmd + Enter' to add a new note"
            />
            <button className="cinematic-button primary" onClick={addNote} disabled={!isPlaying && !pauseTimeRef.current}>
              Add Note (Enter)
            </button>
          </div>
          <div className="notes-list">
            {notes.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', opacity: 0.7 }}>
                No notes yet. Start the timecode counter and press Ctrl/Cmd + Enter to add a note.
              </div>
            ) : (
              <>
                <div className="note-item header">
                  <div className="note-timecode">Timecode</div>
                  <div className="note-content">Notes</div>
                </div>
                {notes.map(note => (
                  <div className="note-item" key={note.id} onDoubleClick={() => setEditingNoteId(note.id)}>
                    <div className="note-timecode">{note.timecode}</div>
                    <div className="note-content">
                      {editingNoteId === note.id ? (
                        <input
                          className="note-edit-input"
                          defaultValue={note.text}
                          onBlur={(e) => {
                            const newText = e.target.value.trim();
                            if (newText) {
                              setNotes(prev => prev.map(n => n.id === note.id ? { ...n, text: newText } : n));
                            }
                            setEditingNoteId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') setEditingNoteId(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <div className="note-text">{note.text}</div>
                      )}
                      <div className="note-actions">
                        <button className="cinematic-button" onClick={(e) => { e.stopPropagation(); setEditingNoteId(note.id); }}>✏️</button>
                        <button className="cinematic-button" onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <div className="app-footer">
          <p>Timecode Notes by <a href="https://www.sangampanta.com" target="_blank" rel="noopener noreferrer">Sangam Panta</a></p>
          <div className="version">V1.1.2</div>
        </div>
      </div>
      <input
        type="file"
        id="fileInput"
        style={{ display: 'none' }}
        accept=".csv,.srt,.txt"
        onChange={handleImport}
      />
      {notification && <div className="notification">{notification}</div>}
    </div>
  );
};

export default TimecodeNotes;
